import { Redirect, Route, RouteProps, useLocation } from 'react-router-dom';

import { PublicRoutes, Routes } from '@/config/routes';
import { useAuth } from '@/context/AuthProvider';
import { useGetIsRouteAccessible } from '@/hooks/useGetIsRouteAccessible';
import { useHomeRoute } from '@/hooks/useHomeRoute';

interface Props extends RouteProps {
  path: Routes;
}

const ConditionalRoute: React.FC<Props> = ({ path, exact, ...routeProps }) => {
  const getIsRouteAccessible = useGetIsRouteAccessible();
  const homeRoute = useHomeRoute();
  const { user, shouldHaveAuthToken, session, isAuthLoading } = useAuth();
  const location = useLocation();

  // If auth is still loading and we have a session (from localStorage) or are expecting auth,
  // show the route. This preserves the current route on refresh while auth is loading.
  if (isAuthLoading && (session || shouldHaveAuthToken)) {
    return <Route path={path as string} exact={exact} {...routeProps} />;
  }

  // Check if the route is accessible for the current user
  if (getIsRouteAccessible(path))
    return <Route path={path as string} exact={exact} {...routeProps} />;

  // If no user and no session, redirect to login
  if (!user && !session)
    return (
      <Redirect
        to={{
          pathname: PublicRoutes.login,
          state: {
            from: location,
          },
        }}
      />
    );

  // If user exists but route is not accessible, redirect to home
  return (
    <Route path={path as string} exact={exact}>
      <Redirect
        to={{
          pathname: homeRoute,
        }}
      />
    </Route>
  );
};

export default ConditionalRoute;
